import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import type { RouterOutputs } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'

/**
 * The whole Boxes on the hub shelf (ticket 45): a partner's Box taken in whole whose
 * Point is closed for now waits at the hub — its code, partner, Point, how many
 * Parcels ride in it, why. The hub's shelf and support's read the same rows
 * (`hubReceiving.ShelfBoxes`, `discrepancies.HubShelfBoxes`). Nothing to decide: the
 * Point's opening releases it. Renders nothing when there are none. In the hub's look:
 * a section title with the count (and, where a page needs it, the explanation behind
 * its «i»), then the table.
 */
export type HubShelfBox = RouterOutputs['hubReceiving']['ShelfBoxes'][number]

export function HubShelfBoxes({
	items,
	title,
	hint,
}: {
	items: readonly HubShelfBox[]
	title: string
	/** Why the Boxes wait and that there is nothing to decide — behind the title's «i». */
	hint?: string
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<HubShelfBox>[]>(
		() => [
			{ id: 'code', header: t('hub.shelfBoxCode'), cell: ({ row }) => <span className='font-mono'>{row.original.code}</span> },
			{
				id: 'reasons',
				header: t('hub.shelfReasons'),
				cell: ({ row }) => (
					<div className='flex flex-wrap gap-1'>
						{row.original.reasons.map((r) => (
							<Badge
								key={r}
								variant='outline'
								className='border-amber-500/60 text-amber-700 dark:text-amber-400'
								data-reason={r}
							>
								{t(`hubShelfReason.${r}`) || r}
							</Badge>
						))}
					</div>
				),
			},
			{ id: 'partner', header: t('hub.shelfPartner'), cell: ({ row }) => row.original.partnerName ?? t('hub.noPartner') },
			{ id: 'point', header: t('hub.shelfPoint'), cell: ({ row }) => row.original.pointCode },
			{
				id: 'parcels',
				header: t('hub.shelfBoxParcels'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row }) => row.original.parcelCount,
			},
		],
		[t],
	)
	const data = items as HubShelfBox[]
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (b) => b.containerId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	if (items.length === 0) return null
	return (
		<section className='flex flex-col gap-2' data-testid='hub-shelf-boxes'>
			<SectionTitle title={title} count={items.length} hint={hint} />
			<DataTable
				table={table}
				withPagination={false}
				row={{ attributes: (b) => ({ 'data-testid': `hub-shelf-box-${b.code}` }) }}
			/>
		</section>
	)
}
